import * as p from '@clack/prompts'
import type { VercelDeployFlowResult } from '@cli-types/next-adapter/vercel.js'
import { redactSecrets } from '@cli-utils/process.js'
import { spinner } from '@cli-utils/terminal.js'
import { guardProjectForDeploy } from '@next-adapter/init/deploy/guard/guard-project-for-deploy.js'
import pc from 'picocolors'
import { ensureVercelAuth } from '../auth/ensure-vercel-auth.js'
import { deployVercelProject } from '../deploy/deploy-vercel-project.js'
import { ensureVercelJsonFramework } from '../deploy/ensure-vercel-json-framework.js'
import { syncVercelProductionEnv } from '../deploy/sync-vercel-production-env.js'
import { guardEnvLocal } from '../env-guard.js'
import { resolveVercelRunner } from '../runner/resolve-vercel-runner.js'
import { authFailureMessage } from './auth-failure-message.js'
import { deployFailureMessage } from './deploy-failure-message.js'
import { ensureLinkedProject } from './ensure-linked-project.js'
import { printManualDeployHint } from './print-manual-deploy-hint.js'

export async function runVercelDeployFlow(options: {
  cwd: string
  projectName: string
  interactive?: boolean
  env?: NodeJS.ProcessEnv
}): Promise<VercelDeployFlowResult> {
  const env = options.env ?? process.env
  const envGuard = guardEnvLocal(options.cwd)

  try {
    const runnerSpinner = spinner()
    runnerSpinner.start('Checking for the Vercel CLI')
    const runner = await resolveVercelRunner()
    runnerSpinner.clear()

    const auth = await ensureVercelAuth(runner, options.cwd, {
      env,
      quietIfAuthed: true,
      allowLogin: options.interactive ?? true
    })
    if (!auth.authed) {
      p.log.warn(authFailureMessage(auth.reason))
      printManualDeployHint()
      return { ok: false }
    }

    await ensureLinkedProject(runner, options.cwd, options.projectName, env, auth.username)

    const envSpinner = spinner()
    envSpinner.start('Syncing environment variables to Vercel')
    const sync = await syncVercelProductionEnv(runner, options.cwd, env)
    envSpinner.clear()
    for (const key of sync.failed) {
      p.log.warn(
        `Could not set ${pc.cyan(key)} on Vercel — add it in the project's environment settings.`
      )
    }

    ensureVercelJsonFramework(options.cwd)

    // Vercel's frozen-lockfile install rejects a lockfile that still lists the removed CLI dep, so
    // the guard regenerates it — that hits the registry, hence the spinner.
    const guardSpinner = spinner()
    guardSpinner.start('Preparing the project for deploy')
    const packageGuard = await guardProjectForDeploy(options.cwd)
    guardSpinner.clear()
    if (packageGuard.lockfile === 'failed') {
      p.log.warn(
        'Could not refresh pnpm-lock.yaml after removing betterstart-cli — the remote install may fail.'
      )
    }
    for (const dep of packageGuard.localSpecDeps) {
      p.log.warn(
        `Dependency ${pc.cyan(dep)} uses a local spec that cannot install on Vercel — the remote build may fail.`
      )
    }

    const deploySpinner = spinner()
    deploySpinner.start('Deploying to Vercel')
    let deploy: Awaited<ReturnType<typeof deployVercelProject>>
    try {
      deploy = await deployVercelProject(runner, options.cwd, {
        env,
        onLogLine: (line) => deploySpinner.message(line)
      })
    } finally {
      packageGuard.restore()
    }
    if (deploy.failure) {
      deploySpinner.stop(`${pc.yellow('▲')} ${deployFailureMessage(deploy.failure)}`)
      if (deploy.detail) p.log.message(pc.dim(redactSecrets(deploy.detail)))
      printManualDeployHint()
      return { ok: false }
    }

    // Vercel normalizes the assigned production domain (dots and other characters in a project
    // name are transformed), so a URL must never be reconstructed from `.vercel/project.json`.
    const url = deploy.url
    deploySpinner.stop(url ? `Deployed ${pc.cyan(url)}` : 'Deployed to Vercel')
    return { ok: true, url, syncedEnvKeys: sync.synced }
  } catch (error) {
    p.log.warn(`Vercel deploy failed: ${error instanceof Error ? error.message : String(error)}`)
    printManualDeployHint()
    return { ok: false }
  } finally {
    envGuard.restore()
  }
}
